import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import equal from "fast-deep-equal";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  HeatOverrideRecipe,
  MaterialQuantity,
  MixBasket,
} from "src/store/api/generatedApi";

import { OverrideIngredientControl } from "./OverrideIngredientControl";
import {
  isValidOverrideQuantity,
  MAX_OVERRIDE_QUANTITY,
  MIN_OVERRIDE_QUANTITY,
} from "./quantity";

interface Props {
  recipe: HeatOverrideRecipe;
  onBack: () => void;
  onSave: (baskets: MixBasket[]) => void;
  saving: {
    isLoading: boolean;
    error: FetchBaseQueryError | SerializedError | undefined;
  };
}

export const OverrideCatalogEditor = ({
  recipe,
  onBack,
  onSave,
  saving,
}: Props) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const [baskets, setBaskets] = useState(recipe.mix.baskets);
  const valid =
    baskets.length > 0 &&
    baskets.every(
      (basket) =>
        basket.layers.some((layer) => layer.quantity.value > 0) &&
        basket.layers.every(
          ({ quantity }) =>
            isValidOverrideQuantity(quantity.value) &&
            (quantity.unit === "ratio" || quantity.unit === "grab")
        )
    );
  const changed = !equal(baskets, recipe.mix.baskets);
  const stale =
    saving.error && "status" in saving.error && saving.error.status === 409;

  const changeQuantity = ({
    basketNumber,
    layerNumber,
    quantity,
  }: {
    basketNumber: number;
    layerNumber: number;
    quantity: MaterialQuantity;
  }) => {
    setBaskets((previous) =>
      previous.map((basket) =>
        basket.basket_number !== basketNumber
          ? basket
          : {
              ...basket,
              layers: basket.layers.map((layer) =>
                layer.layer_number !== layerNumber
                  ? layer
                  : { ...layer, quantity }
              ),
            }
      )
    );
  };

  return (
    <Stack spacing={2}>
      {saving.error && (
        <Alert severity="error">
          {t(stale ? "overrideRecipeChanged" : "overrideRecipeSaveError")}
        </Alert>
      )}
      {baskets.map((basket) => (
        <Box key={basket.basket_number}>
          <Box
            sx={{
              overflowX: "auto",
              border: 1,
              borderColor: "text.primary",
              borderRadius: 1,
              bgcolor: "grey.50",
            }}
          >
            <Typography
              component="div"
              variant="h4"
              sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}
            >
              {recipe.name}
              {baskets.length > 1 &&
                ` · ${t("basket")} ${basket.basket_number}`}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${basket.layers.length}, minmax(0, 1fr))`,
                gridTemplateRows: "auto auto",
                minWidth: basket.layers.length * 90,
              }}
            >
              {basket.layers.map((layer) => (
                <OverrideIngredientControl
                  key={layer.layer_number}
                  materialName={layer.material_name}
                  quantity={layer.quantity}
                  disabled={saving.isLoading}
                  onChange={(quantity) => {
                    changeQuantity({
                      basketNumber: basket.basket_number,
                      layerNumber: layer.layer_number,
                      quantity,
                    });
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ))}
      {!valid && (
        <Alert severity="info">
          {t("overrideRecipeQuantitiesInvalid", {
            min: formatNumber(0)(MIN_OVERRIDE_QUANTITY),
            max: formatNumber(0)(MAX_OVERRIDE_QUANTITY),
          })}
        </Alert>
      )}
      <Stack direction="row" sx={{ gap: 1, justifyContent: "flex-end" }}>
        <Button onClick={onBack} disabled={saving.isLoading}>
          {t("back")}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(baskets);
          }}
          disabled={!valid || !changed || saving.isLoading || Boolean(stale)}
        >
          {t("save")}
        </Button>
      </Stack>
    </Stack>
  );
};
